import React, {
  useState, useMemo, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { withPolaris } from 'Components/protons/Polaris';
import './_style.scss';
import { SlideshowCarousel } from 'Components/molecules/SlideshowCarousel';
import { SlideshowSlider } from 'Components/molecules/SlideshowSlider';

const Slideshow = ({
  media,
  context: { cssPrefix },
}) => {
  if (!media || media.length === 0) return null;

  const [activeIndex, setActiveIndex] = useState(0);
  const slideshowRef = useRef(null);
  const viewportStartTime = useRef(null);
  const totalViewTime = useRef(0);
  const isInViewport = useRef(false);
  const viewedImages = useRef(new Set([0])); // Start with first image as viewed
  const imageViewTimes = useRef({}); // Track time spent on each image
  const currentImageStartTime = useRef(null);
  const previousIndex = useRef(0); // Track the previous index
  const viewportSessions = useRef([]); // Track all viewport entry/exit sessions
  const hasEverLeftViewport = useRef(false); // Track if user has ever scrolled away

  // Track viewed images and time spent on each image when currentIndex changes
  useEffect(() => {
    const currentTime = performance.now();

    // Record time spent on previous image (if slideshow is in viewport)
    if (currentImageStartTime.current && isInViewport.current) {
      const timeSpent = currentTime - currentImageStartTime.current;

      // Only record if we spent at least 100ms on the image (avoid quick scrolling)
      if (timeSpent >= 100) {
        const imageKey = `image_${previousIndex.current}`;
        imageViewTimes.current[imageKey] = (imageViewTimes.current[imageKey] || 0) + timeSpent;
      }
    }

    // Update previous index to current before we change timing
    previousIndex.current = activeIndex;

    // Track new image as viewed and start timing
    viewedImages.current.add(activeIndex);
    if (isInViewport.current) {
      currentImageStartTime.current = currentTime;
    }
  }, [activeIndex]);

  // Viewport tracking for analytics
  useEffect(() => {
    const element = slideshowRef.current;
    if (!element) {
      return undefined;
    }

    const sendViewTimeToDataLayer = (duration) => {
      // Calculate average time per viewed image
      const totalImageTime = Object.values(imageViewTimes.current).reduce((sum, time) => sum + time, 0);
      const averageTimePerImage = viewedImages.current.size > 0
        ? Math.round(totalImageTime / viewedImages.current.size)
        : 0;

      // Send to dataLayer for Google Analytics/GTM
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'slideshow.view_time',
          'slideshow.duration_ms': duration,
          'slideshow.duration_seconds': Math.round(duration / 1000),
          'slideshow.media_count': media.length,
          'slideshow.images_viewed': viewedImages.current.size,
          'slideshow.completion_rate': Math.round((viewedImages.current.size / media.length) * 100),
          'slideshow.image_view_times': { ...imageViewTimes.current },
          'slideshow.average_time_per_image_ms': averageTimePerImage,
          'slideshow.average_time_per_image_seconds': Math.round(averageTimePerImage / 1000),
          'slideshow.return_visits': Math.max(0, viewportSessions.current.length - 1),
          'slideshow.total_sessions': viewportSessions.current.length,
          'slideshow.user_returned': hasEverLeftViewport.current && viewportSessions.current.length > 1,
        });
      }
    };

    const handleSlideshowEnterViewport = (currentTime) => {
      isInViewport.current = true;
      viewportStartTime.current = currentTime;
      currentImageStartTime.current = currentTime; // Start timing current image

      // Track this as a new session
      const sessionNumber = viewportSessions.current.length + 1;
      const isReturnVisit = hasEverLeftViewport.current;

      // Reset session-specific analytics data for return visits
      if (isReturnVisit) {
        totalViewTime.current = 0; // Reset total time for new session
        imageViewTimes.current = {}; // Reset image times for new session
        viewedImages.current = new Set([activeIndex]); // Reset viewed images, starting with current
      }

      viewportSessions.current.push({
        sessionNumber,
        startTime: currentTime,
        isReturnVisit,
      });

      // Send immediate return visit event if this is a return
      if (isReturnVisit && typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'slideshow.return_visit',
          'slideshow.session_number': sessionNumber,
          'slideshow.return_visit_count': sessionNumber - 1,
          'slideshow.time_since_last_exit': viewportSessions.current.length > 1
            ? currentTime - (viewportSessions.current[viewportSessions.current.length - 2].endTime || 0)
            : 0,
        });
      }
    };

    const recordCurrentImageTime = (currentTime) => {
      if (!currentImageStartTime.current) return;

      const imageTime = currentTime - currentImageStartTime.current;
      if (imageTime >= 100) {
        const imageKey = `image_${previousIndex.current}`;
        imageViewTimes.current[imageKey] = (imageViewTimes.current[imageKey] || 0) + imageTime;
      }
      currentImageStartTime.current = null;
    };

    const handleSlideshowExitViewport = (currentTime) => {
      recordCurrentImageTime(currentTime);
      isInViewport.current = false;
      hasEverLeftViewport.current = true; // Mark that user has left at least once

      if (!viewportStartTime.current) return;

      const sessionDuration = currentTime - viewportStartTime.current;
      totalViewTime.current += sessionDuration;

      // Update the current session with end time and duration
      if (viewportSessions.current.length > 0) {
        const currentSession = viewportSessions.current[viewportSessions.current.length - 1];
        currentSession.endTime = currentTime;
        currentSession.duration = sessionDuration;
      }

      // Send data to analytics if user spent at least 1 second viewing
      if (totalViewTime.current >= 1000) {
        sendViewTimeToDataLayer(totalViewTime.current);
      }
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const currentTime = performance.now();

        // Handle slideshow entering viewport
        if (entry.isIntersecting && !isInViewport.current) {
          handleSlideshowEnterViewport(currentTime);
          return;
        }

        // Handle slideshow leaving viewport
        if (!entry.isIntersecting && isInViewport.current) {
          handleSlideshowExitViewport(currentTime);
        }
      });
    };

    // Create intersection observer with 50% threshold
    // This ensures we track when at least half the slideshow is visible
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.5,
      rootMargin: '0px',
    });

    observer.observe(element);

    // Cleanup function to send final data when component unmounts
    return () => {
      observer.disconnect();

      // Send final view time if user was viewing when component unmounts
      if (isInViewport.current && viewportStartTime.current) {
        const currentTime = performance.now();

        // Record time for current image before sending final analytics
        if (currentImageStartTime.current) {
          const finalImageTime = currentTime - currentImageStartTime.current;
          if (finalImageTime >= 100) {
            const imageKey = `image_${previousIndex.current}`;
            imageViewTimes.current[imageKey] = (imageViewTimes.current[imageKey] || 0) + finalImageTime;
          }
        }

        const finalDuration = currentTime - viewportStartTime.current;
        totalViewTime.current += finalDuration;

        if (totalViewTime.current >= 1000) {
          sendViewTimeToDataLayer(totalViewTime.current);
        }
      }
    };
  }, [media.length]);

  const sliderMedia = useMemo(() => media.map((item) => ({
    src: item.thumb,
    alt: item.alt || '',
  })), [media]);

  return (
    <div className={`${cssPrefix}__slideshow`} ref={slideshowRef}>
      <SlideshowCarousel
        media={media}
        goToIndex={activeIndex}
        onIndexChange={setActiveIndex}
      />
      <SlideshowSlider
        media={sliderMedia}
        currentIndex={activeIndex}
        setCurrentIndex={setActiveIndex}
      />
    </div>
  );
};

Slideshow.propTypes = {
  media: PropTypes.arrayOf(PropTypes.shape({
    thumb: PropTypes.string,
    large: PropTypes.string,
    caption: PropTypes.string,
    credits: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isLCPCandidate: PropTypes.bool,
  })).isRequired,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }).isRequired,
};

export default withPolaris(Slideshow);
